import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoMdClose } from "react-icons/io";
import { ChevronDown, ChevronUp } from "lucide-react";

import { logout } from "../../store";
import LogoDark from "../../assets/images/logo-dark.png";
import LogoLight from "../../assets/images/logo-light.png";
import Input from "../pagesSpecific/Login/Input";
import LogoComponent from "../Reusable/LogoComponent";
import { ReactComponent as SearchIcon } from "../../assets/icons/Search.svg";
import IconWrapper from "./IconWrapper";
import { useTheme } from "../../context/ThemeContext";

const SideBar = ({
  isMobile = false,
  isSidebarOpen = true,
  setIsSidebarOpen,
  activeTab = "",
  menuItems,
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const { theme } = useTheme();

  // Check if any submenu is active using EXACT path matching
  const isSubmenuActive = (subMenu) =>
    subMenu.some((subItem) => pathname === subItem.link);

  // Auto-open submenu if one of its items is active
  useEffect(() => {
    const newOpenSubmenus = {};
    menuItems?.forEach((item) => {
      if (item.hasSubMenu) {
        const hasActiveSubitemByPath = isSubmenuActive(item.subMenu);

        if (hasActiveSubitemByPath) {
          newOpenSubmenus[item.title] = true;
        }
      }
    });
    setOpenSubmenus(newOpenSubmenus);
  }, [pathname, menuItems]);

  const closeSidebar = () => setIsSidebarOpen(false);

  const toggleSubmenu = (itemTitle) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [itemTitle]: !prev[itemTitle],
    }));
  };

  // Check if main menu item is active
  const isMainItemActive = (item) => {
    if (item.hasSubMenu) return false;

    if (!item.link) return false;

    // For routes with base paths (like /admin/orders that matches /admin/orders/*)
    // Check if current pathname starts with the base path
    if (pathname.startsWith(item.link)) {
      // Make sure it's not just a partial match
      // e.g., /admin/orders-something shouldn't match /admin/orders
      const nextChar = pathname.charAt(item.link.length);
      return nextChar === "" || nextChar === "/";
    }

    return false;
  };

  // Check if submenu item is active (exact match only)
  const isSubItemActive = (subItem) => {
    return pathname === subItem.link;
  };

  // Check if parent menu should be highlighted (when any submenu item is active)
  const isSubmenuHighlighted = (item) => {
    if (item.hasSubMenu) {
      return item.subMenu.some((sub) => pathname === sub.link);
    }
    return false;
  };

  const { counts } = useSelector((state) => state.order);

  return (
    <>
      {/* Backdrop Overlay - Mobile Only */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[998]"
          onClick={closeSidebar}
        />
      )}

      <aside
        className={`w-[280px] fixed top-0 left-0 h-dvh z-[999] border-r border-[var(--color-stroke-01)] transition-all duration-300 bg-[var(--color-background-01)] ${
          isMobile
            ? isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-[280px]"
            : "translate-x-0"
        }`}
      >
        <div className="relative h-full">
          {/* Close Icon - Mobile Only */}
          {isMobile && isSidebarOpen && (
            <div className="absolute z-10 cursor-pointer top-5 right-5">
              <IoMdClose
                className="text-2xl text-[var(--color-content)] transition-colors hover:text-[var(--color-primary)]"
                onClick={closeSidebar}
              />
            </div>
          )}

          <div className="flex flex-col h-full py-6 sm:py-8 lg:py-10">
            {/* Logo Section */}
            <div className="flex items-center justify-center w-full h-[47px] py-2 px-6 mb-4 sm:mb-5 lg:mb-10">
              <div className=" w-[100px] h-[27px]">
                <LogoComponent src={theme === "dark" ? LogoDark : LogoLight} alt="icieos Logo" link="/" />
              </div>
            </div>

            {/* Search Section */}
            <div className="px-4 mb-4 sm:px-6 sm:mb-5 lg:mb-10">
              <Input
                type="text"
                placeholder="Search for..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                icon={
                  <div className="absolute -translate-y-1/2 left-1 top-1/2 text-[var(--color-content-01)]">
                    <SearchIcon width={16} height={16} />
                  </div>
                }
                iconPosition="left"
                className="w-full h-[42px] rounded-lg border border-[var(--color-stroke-01)] bg-transparent px-3 py-2 pl-10 text-base font-inter font-normal text-[var(--color-content-03)] focus:outline-none placeholder-[var(--color-content-03)]"
              />
            </div>

            {/* Navigation Section */}
            <div className="flex-1 py-2 overflow-y-auto">
              <nav className="px-6">
                <ul className="space-y-2">
                  {menuItems?.map((item) => {
                    const isActive = isMainItemActive(item);
                    const isSubmenuOpen = openSubmenus[item.title];
                    const isHighlighted = isSubmenuHighlighted(item);

                    return (
                      <li
                        key={item.title}
                        onClick={() => {
                          if (item?.title === "Logout") dispatch(logout());
                        }}
                      >
                        {/* Main Menu Item */}
                        {item.hasSubMenu ? (
                          <button
                            className={`group w-full h-[44px] sm:h-[40px] flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 text-left ${
                              isSubmenuOpen || isHighlighted
                                ? "text-[var(--sidebar-text-active-color)] sidebar-gradient"
                                : "text-[var(--sidebar-text-color)] hover:text-[var(--sidebar-text-active-color)] sidebar-gradient-hover"
                            }`}
                            onClick={() => toggleSubmenu(item.title)}
                          >
                            <div className="flex items-center">
                              <div className=" flex items-center gap-3">
                                <IconWrapper
                                  icon={item.icon}
                                  className="w-5 h-5 sm:w-6 sm:h-6"
                                />
                                <span className="text-sm font-normal sm:text-base">
                                  {item.title}
                                </span>
                              </div>

                              {item.badge && (
                                <span className="ml-2 px-2 py-1 text-xs font-semibold min-w-[20px] text-center bg-purple-600 text-white">
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center">
                              {isSubmenuOpen ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </div>
                          </button>
                        ) : (
                          <Link
                            to={item.link}
                            className={`group w-full h-[44px] sm:h-[40px] flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                              isActive
                                ? "text-[var(--sidebar-text-active-color)] sidebar-gradient"
                                : "text-[var(--sidebar-text-color)] hover:text-[var(--sidebar-text-active-color)] sidebar-gradient-hover"
                            }`}
                            onClick={() => {
                              if (isMobile) setIsSidebarOpen(false);
                            }}
                          >
                            <div className=" flex items-center gap-3">
                              <IconWrapper
                                icon={item.icon}
                                className="w-5 h-5 sm:w-6 sm:h-6"
                              />
                              <span className="text-sm font-normal sm:text-base">
                                {item.title}
                              </span>
                            </div>

                            {item.badge && counts.inProgress >= 1 && (
                              <span className="ml-auto flex items-center justify-center rounded text-xs font-semibold bg-[#7B02CB] text-white h-[24px] min-w-[24px] text-center">
                                {counts?.inProgress}
                              </span>
                            )}
                          </Link>
                        )}

                        {/* Submenu Items */}
                        {item.hasSubMenu && isSubmenuOpen && (
                          <ul className="mt-1.5 sm:mt-2 space-y-1">
                            {item.subMenu.map((subItem) => {
                              const isSubActive = isSubItemActive(subItem);

                              return (
                                <li key={subItem.link}>
                                  <Link
                                    to={subItem.link}
                                    className={`group w-full h-[44px] sm:h-[40px] flex items-center justify-between px-3 py-2 pl-11 sm:pl-12 rounded-lg transition-all duration-200 ${
                                      isSubActive
                                        ? "text-[var(--color-label)] sidebar-gradient"
                                        : "text-[var(--color-content-03)] hover:text-[var(--color-label)] sidebar-gradient-hover"
                                    }`}
                                    onClick={() => {
                                      if (isMobile) setIsSidebarOpen(false);
                                    }}
                                  >
                                    <span className="text-sm font-normal leading-5 sm:text-base sm:leading-6">
                                      {subItem.title}
                                    </span>
                                    {subItem.badge && (
                                      <span className="px-2 py-1 text-xs font-semibold bg-purple-600 text-white rounded-full min-w-[20px] text-center">
                                        {subItem.badge}
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* Sidebar Footer Text Section */}
            <div className="absolute bottom-0 left-0 w-full px-6 mb-4 sm:mb-5 sm:px-9">
              <div
                className="flex items-center justify-center text-center rounded-lg"
                style={{
                  color: "var(--color-content-03)",
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "11px",
                  lineHeight: "18px",
                }}
              >
                icieos@2025. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SideBar;